import { Injectable } from '@angular/core';
import { Appliance, RemoteData } from '@common/util-models';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import {
  BrandsByApplianceState,
  DeviceCategoryTypesRecord,
} from '@sky/util-models';
import { Observable } from 'rxjs';
import * as AppliancesActions from './appliances/appliances.actions';
import * as AppliancesSelectors from './appliances/appliances.selectors';
import * as BrandsActions from './brands/brands.actions';
import * as BrandsSelectors from './brands/brands.selectors';
import { CatalogState } from './catalog.reducer';

@Injectable()
export class CatalogFacade {
  baseAppliances$: Observable<Appliance[]> = this.store.pipe(
    select(AppliancesSelectors.getBaseAppliances)
  );

  appliances$: Observable<Appliance[]> = this.store.pipe(
    select(AppliancesSelectors.getAppliances)
  );

  appliancesEntities$: Observable<Dictionary<Appliance>> = this.store.pipe(
    select(AppliancesSelectors.getApplianceEntities)
  );

  selectedSkyBox$: Observable<Appliance> = this.store.pipe(
    select(AppliancesSelectors.getSelectedSkyBox),
    filterNullUndefined()
  );

  applianceCategories$: Observable<string[]> = this.store.pipe(
    select(AppliancesSelectors.getApplianceCategories)
  );

  applianceCategoryTypes$: Observable<DeviceCategoryTypesRecord> =
    this.store.pipe(select(AppliancesSelectors.getApplianceCategoryTypes));

  appliancesLoaded$: Observable<RemoteData> = this.store.pipe(
    select(AppliancesSelectors.getAppliancesLoaded)
  );

  brandsByApplianceState$: Observable<BrandsByApplianceState> = this.store.pipe(
    select(BrandsSelectors.getBrandsByApplianceState)
  );

  constructor(private store: Store<CatalogState>) {}

  setBaseAppliance(appliance: Appliance) {
    this.store.dispatch(AppliancesActions.selectSkyBox({ appliance }));
  }

  loadBrands(applianceCode: string) {
    this.store.dispatch(BrandsActions.loadBrands({ applianceCode }));
  }

  categoryByApplianceCode(applianceCode: string) {
    return this.store
      .select(AppliancesSelectors.getCategoryByApplianceCode({ applianceCode }))
      .pipe(filterNullUndefined());
  }

  typeByApplianceCode(applianceCode: string) {
    return this.store
      .select(AppliancesSelectors.getTypeByApplianceCode({ applianceCode }))
      .pipe(filterNullUndefined());
  }

  applianceByCode(applianceCode: string) {
    return this.store.select(
      AppliancesSelectors.getApplianceByCode({ applianceCode })
    );
  }

  applianceByLTCode(applianceLTCode: string) {
    return this.store.select(
      AppliancesSelectors.getApplianceByLTCode({ applianceLTCode })
    );
  }

  brandName(applianceCode: string, brandCode: string) {
    return this.store.select(
      BrandsSelectors.getBrandName({ applianceCode, brandCode })
    );
  }

  brandRemoteState(applianceCode: string) {
    return this.store.select(
      BrandsSelectors.getBrandRemoteState({ applianceCode })
    );
  }

  brandNameByDescription(applianceCode: string, brandDescription: string) {
    return this.store.select(
      BrandsSelectors.getBrandByDescription({ applianceCode, brandDescription })
    );
  }
}
